import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ErroApi } from '../api';
import { rotulo } from '../formato';
import { Area, Aviso, Campo, Dialogo, ErroDaApi } from '../ui';
import { useSessao } from '../sessao';
import {
  EscolhaDeObra, SeletorResponsavelTecnico, useResponsaveisTecnicos, type Os,
} from './OsForms';

/**
 * Abrir uma ordem de serviço.
 *
 * É tela, e não diálogo, por causa da cadeia que ela pode ter que
 * atravessar: **OS → obra → cliente**. Quem chega aqui com uma obra nova
 * de um cliente novo precisa criar os três, e três diálogos empilhados no
 * celular são três telas cobrindo uma à outra — fechar a de cima por
 * engano perde as três.
 *
 * O que esta tela NÃO faz é numerar. O número da OS é o número do projeto
 * da obra (`AAMMNNN`, sete dígitos), digitado no cadastro da obra e vindo
 * do controle da Testesolo. Por isso não existe campo de número aqui: o
 * que existe é o campo da obra, e escolher a obra é escolher o número.
 *
 * Só a obra é obrigatória. Prazo, RT e prioridade podem entrar depois
 * — exigi-los agora empurraria quem está abrindo a OS a inventar uma data
 * para conseguir gravar, e prazo inventado é pior que prazo em branco:
 * ele aparece na lista de atrasos como se fosse compromisso.
 */
/*
 * O rascunho sobrevive ao F5 (UX-004).
 *
 * A auditoria preencheu a OS inteira, apertou F5 e perdeu tudo. A tela é
 * longa e a obra pode ter sido cadastrada no caminho; recomeçar do zero
 * é o que faz alguém abrir a OS «depois» — e depois é quando a amostra
 * já chegou sem OS. O rascunho fica no navegador, só neste aparelho,
 * até a OS ser aberta ou o «Cancelar» ser tocado.
 */
const CHAVE_RASCUNHO = 'testesolo.rascunho.os-nova';

interface RascunhoOs {
  obra: ObraEscolhida | null;
  proposta: string; prioridade: string; prazo: string; inicio: string;
  fim: string; guarda: string;
  observacoes: string; motivoLote: string; rt?: string;
}

type ObraEscolhida = {
  id: string; codigo: string; nome: string;
  cliente_id: string; municipio: string | null; uf: string | null;
};

function lerRascunho(): Partial<RascunhoOs> {
  try {
    const bruto = sessionStorage.getItem(CHAVE_RASCUNHO)
      ?? localStorage.getItem(CHAVE_RASCUNHO);
    return bruto ? (JSON.parse(bruto) as Partial<RascunhoOs>) : {};
  } catch { return {}; }
}

function guardarRascunho(r: RascunhoOs) {
  try { localStorage.setItem(CHAVE_RASCUNHO, JSON.stringify(r)); } catch { /* sem espaço */ }
}

function apagarRascunho() {
  try { localStorage.removeItem(CHAVE_RASCUNHO); sessionStorage.removeItem(CHAVE_RASCUNHO); }
  catch { /* nada */ }
}

export function NovaOs() {
  const navegar = useNavigate();
  const [rascunho] = useState(() => lerRascunho());
  const [restaurado] = useState(() => !!rascunho.obra);

  const [obra, setObra] = useState<ObraEscolhida | null>(rascunho.obra ?? null);
  /*
   * O lote (`0041`).
   *
   * Não é a tela que escolhe a letra: ela PERGUNTA ao banco qual está
   * livre assim que a obra é escolhida. Se duas pessoas abrirem lote ao
   * mesmo tempo, as duas recebem a mesma sugestão e a restrição do banco
   * recusa a segunda — que é o comportamento certo. Escolher na tela e
   * confiar nisso é que seria errado.
   */
  const [lote, setLote] = useState<Lote | null>(null);
  /*
   * O motivo do segundo lote (UX-062). A Testesolo decidiu em 03/09/2026:
   * a obra PODE ter mais de uma OS, «com confirmação» — a tela avisa qual
   * OS a obra já tem, pergunta se é para abrir outra, e exige o porquê.
   * A frase vai para a trilha da OS nova; sem ela a API recusa (400).
   */
  const [motivoLote, setMotivoLote] = useState(rascunho.motivoLote ?? '');
  const [confirmando, setConfirmando] = useState(false);
  const [proposta, setProposta] = useState(rascunho.proposta ?? '');
  const [prioridade, setPrioridade] = useState(rascunho.prioridade ?? 'normal');
  const [prazo, setPrazo] = useState(rascunho.prazo ?? '');
  const [inicio, setInicio] = useState(rascunho.inicio ?? '');
  const [fim, setFim] = useState(rascunho.fim ?? '');
  const [guarda, setGuarda] = useState(rascunho.guarda ?? '');
  const [observacoes, setObservacoes] = useState(rascunho.observacoes ?? '');
  /*
   * O responsável técnico já na abertura (225) — «Responsável técnico deve
   * ser definido também na abertura da OS» (Humberto, 29/09/2026). Quem
   * define continua sendo quem tem a permissão (RN-194): para os outros
   * o seletor não aparece, e nem se pede a lista de funcionários.
   */
  const { eu, pode } = useSessao();
  const podeDefinirRt = pode('os.definir_responsavel_tecnico');
  const { lista: responsaveis, semPermissao } = useResponsaveisTecnicos(podeDefinirRt);
  const [rt, setRt] = useState(rascunho.rt ?? '');
  /* RN-227 (274): só quem tem a assinatura de RT. Um rascunho guardado
     antes da regra — ou de alguém que perdeu a assinatura — pode trazer
     um RT que não está mais na lista: volta a «não definido», em vez de
     ir ao servidor e voltar recusado. */
  useEffect(() => {
    if (responsaveis && rt && !responsaveis.some((f) => f.id === rt)) setRt('');
  }, [responsaveis, rt]);

  /* Cada mudança vai para o rascunho; sem obra não há o que guardar. */
  useEffect(() => {
    if (!obra) { apagarRascunho(); return; }
    guardarRascunho({ obra, proposta, prioridade, prazo, inicio, fim, guarda,
      observacoes, motivoLote, rt });
  }, [obra, proposta, prioridade, prazo, inicio, fim, guarda,
    observacoes, motivoLote, rt]);
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!obra) { setLote(null); setMotivoLote(''); return; }
    api<Lote>(`/os/proximo-lote?obra=${obra.id}`)
      .then(setLote)
      // Falhar aqui não pode travar a abertura: sem a sugestão, a OS
      // nasce sem letra, que é o caso comum e o que já valia antes.
      .catch(() => setLote(null));
  }, [obra]);

  const erroApi = erro instanceof ErroApi ? erro : null;
  const guardaNum = guarda.trim() ? Number(guarda) : undefined;
  const guardaOk = guardaNum === undefined
    || (Number.isInteger(guardaNum) && guardaNum >= 1 && guardaNum <= 3650);
  const datasOk = !inicio || !fim || inicio <= fim;
  const segundoLote = !!lote?.tem_os;
  const motivoOk = !segundoLote || motivoLote.trim().length >= 5;
  const numeroNovo = obra
    ? (segundoLote && lote?.complemento
      ? `${obra.codigo}-${lote.complemento}` : obra.codigo)
    : '';
  const existentes = (lote?.existentes ?? []).join(', ');

  async function abrir() {
    setErro(null);
    setEnviando(true);
    try {
      const os = await api<Os>('/os', {
        metodo: 'POST',
        corpo: {
          obraId: obra!.id,
          complemento: lote?.complemento ?? undefined,
          propostaNumero: proposta.trim() || undefined,
          prioridade,
          prazoContratual: prazo || undefined,
          dataPrevistaInicio: inicio || undefined,
          dataPrevistaFim: fim || undefined,
          prazoGuardaDias: guardaNum,
          observacoes: observacoes.trim() || undefined,
          motivo: segundoLote ? motivoLote.trim() : undefined,
          responsavelTecnicoId: podeDefinirRt && rt ? rt : undefined,
        },
      });
      apagarRascunho();
      navegar(`/os/${os.id}`);
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <div>
      <div className="cabeca">
        <h1>Nova ordem de serviço</h1>
        <p className="sub">
          A OS nasce da obra — e leva o número do projeto dela
        </p>
      </div>

      <ErroDaApi erro={erro} />

      {restaurado && (
        <Aviso tipo="info" titulo="Continuando de onde você parou.">
          O que estava preenchido antes de a página recarregar voltou.
          Para começar do zero, toque em <b>Cancelar</b>.
        </Aviso>
      )}

      <div className="cartao">
        <h2>De quem é o serviço</h2>
        <EscolhaDeObra obra={obra} aoEscolher={setObra}
          numeroPrevisto={numeroNovo || undefined} />
        {/*
          O aviso fica AQUI, colado na obra, e não no fim da tela: quem
          escolheu a obra errada — ou quis dizer «revisão» e não «lote» —
          precisa descobrir isso antes de preencher dez campos (UX-062).
        */}
        {obra && segundoLote && (
          <Aviso tipo="atencao"
            titulo={`Esta obra já tem a OS ${existentes || obra.codigo}. `
              + `Abrir ${numeroNovo}?`}>
            O número do projeto é o mesmo — muda só a letra do lote. Use
            isto quando chegar material novo do mesmo projeto que não
            estava previsto. Se o que mudou foi o <b>escopo</b> de
            um lote que já existe, não abra OS nova: abra uma{' '}
            <b>revisão</b> na ficha daquela OS, para o histórico
            do que foi contratado não se partir em duas.
            <Area
              rotulo="Motivo do novo lote"
              rows={2}
              value={motivoLote}
              onChange={(e) => setMotivoLote(e.target.value)}
              erro={erroApi?.campo('motivo')
                ?? (motivoLote.trim().length > 0 && !motivoOk
                  ? 'Escreva ao menos cinco caracteres.' : undefined)}
              dica="Obrigatório. Fica na trilha da OS nova — é o que explica, daqui a um ano, por que este projeto tem duas ordens."
            />
          </Aviso>
        )}
      </div>

      {/*
        O formulário inteiro, de cara. Até 04/09/2026 o resto só aparecia
        depois da obra — «ao clicar em Nova OS já poderia abrir o
        formulário completo» (Humberto). A obra continua obrigatória: é o
        botão que espera por ela, não a tela.
      */}
      {(
        <>
          <div className="cartao">
            <h2>Prazos</h2>
            <Campo
              rotulo="Prazo contratual"
              type="date"
              value={prazo}
              onChange={(e) => setPrazo(e.target.value)}
              erro={erroApi?.campo('prazoContratual')}
              dica="A data prometida ao cliente. É ela que faz a OS aparecer como atrasada — e é a única data que faz isso."
            />
            <div className="linha">
              <Campo rotulo="Início previsto" type="date" value={inicio}
                onChange={(e) => setInicio(e.target.value)}
                erro={erroApi?.campo('dataPrevistaInicio')} />
              <Campo rotulo="Fim previsto" type="date" value={fim}
                onChange={(e) => setFim(e.target.value)}
                erro={erroApi?.campo('dataPrevistaFim')
                  ?? (!datasOk ? 'O fim previsto está antes do início.' : undefined)} />
            </div>
            <Campo
              rotulo="Prazo de guarda das amostras (dias)"
              inputMode="numeric"
              value={guarda}
              onChange={(e) => setGuarda(e.target.value.replace(/\D/g, ''))}
              erro={erroApi?.campo('prazoGuardaDias')
                ?? (!guardaOk ? 'De 1 a 3650 dias.' : undefined)}
              dica="Em branco usa o padrão da casa: 15 dias depois do laudo aprovado. O contrato pode dizer outro número."
            />
          </div>

          {/*
            Quem responde (225). «Aberta por» não é campo: é quem está
            abrindo, e o banco grava sozinho na trilha (0002) — o PDF da OS
            lê de lá. Não se pede à pessoa que diga quem ela é. O
            responsável técnico, sim, se escolhe aqui — e só por quem tem
            a permissão (RN-194); os outros abrem sem RT e o administrador
            define depois, em «Dados da OS» → «Editar».
          */}
          <div className="cartao">
            <h2>Quem responde</h2>
            <p className="sub" data-aberta-por>
              <b>Aberta por:</b> {eu?.nome ?? 'você'} — gravado
              automaticamente com a OS.
            </p>
            {podeDefinirRt ? (
              <SeletorResponsavelTecnico
                valor={rt} aoEscolher={setRt}
                responsaveis={responsaveis} semPermissao={semPermissao}
                erro={erroApi?.campo('responsavelTecnicoId')}
                dica="Quem assina o relatório — só quem tem a assinatura de RT (RN-227). Pode ficar em branco e ser definido depois, em «Editar». Só o administrador do sistema define ou troca (RN-194)."
              />
            ) : (
              <p className="sub">
                <b>Responsável técnico:</b> definido pelo administrador do
                sistema, depois de aberta a OS.
              </p>
            )}
          </div>

          <div className="cartao">
            <h2>Contrato e observações</h2>
            <Campo
              rotulo="Proposta ou contrato"
              value={proposta}
              onChange={(e) => setProposta(e.target.value)}
              erro={erroApi?.campo('propostaNumero')}
              dica="O número da proposta comercial que originou o serviço."
            />
            <label className="campo">
              <span className="rotulo">Prioridade</span>
              <select value={prioridade}
                onChange={(e) => setPrioridade(e.currentTarget.value)}>
                {['baixa', 'normal', 'alta', 'urgente'].map((p) => (
                  <option key={p} value={p}>{rotulo(p)}</option>
                ))}
              </select>
            </label>
            <Area rotulo="Observações" rows={3} value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)} />
          </div>

          {obra && !segundoLote && (
            <Aviso tipo="info" titulo={`A OS vai se chamar ${obra.codigo}.`}>
              O número é o do projeto da obra e não muda depois. A programação
              das amostras e o escopo entram na ficha, depois de aberta.
            </Aviso>
          )}

          <div className="acoes-fixas">
            <button
              type="button" className="botao grande"
              disabled={!obra || !guardaOk || !datasOk || !motivoOk || enviando}
              title={!obra ? 'Escolha a obra: é ela que dá o número à OS.'
                : !motivoOk
                  ? 'Esta obra já tem OS: informe o motivo do novo lote.' : undefined}
              onClick={() => {
                if (segundoLote) setConfirmando(true);
                else void abrir();
              }}
            >
              {enviando ? 'Abrindo…'
                : !obra ? 'Escolha a obra para abrir'
                  : segundoLote ? `Abrir ${numeroNovo}` : 'Abrir ordem de serviço'}
            </button>
            <Link to="/os" className="botao secundario estreito"
              onClick={apagarRascunho}>Cancelar</Link>
          </div>

          {/*
            A confirmação do segundo lote (UX-062). É diálogo, e não só um
            aviso, porque o aviso já existia e a OS 2609901-A nasceu mesmo
            assim na auditoria de 03/09/2026: aviso que não pede resposta é
            aviso que se passa por cima.
          */}
          <Dialogo
            aberto={confirmando}
            titulo={`Abrir ${numeroNovo}?`}
            aoFechar={() => setConfirmando(false)}
          >
            <p>
              Esta obra já tem a OS <b className="mono">{existentes || obra?.codigo}</b>.
              A nova vai se chamar <b className="mono">{numeroNovo}</b> — mesmo
              projeto, outro lote.
            </p>
            <p className="sub">Motivo: {motivoLote.trim()}</p>
            <div className="linha" style={{ marginTop: '.6rem' }}>
              <button type="button" className="botao" disabled={enviando}
                onClick={() => { setConfirmando(false); void abrir(); }}>
                {enviando ? 'Abrindo…' : `Sim, abrir ${numeroNovo}`}
              </button>
              <button type="button" className="botao secundario"
                onClick={() => setConfirmando(false)} disabled={enviando}>
                Voltar
              </button>
            </div>
          </Dialogo>
        </>
      )}
    </div>
  );
}

interface Lote {
  complemento: string | null;
  tem_os: boolean;
  quantas: number;
  /** Os números das OS que a obra já tem — «2609901», «2609901-A»… */
  existentes?: string[];
}
